/**
 * ExercisesPage — страница «Каталог упражнений» (открывается из меню Лендинга
 * И по пилюле «Тренажёры» в ЛК пациента).
 * Шапка/подвал лендинга — как были; сам каталог — ЕДИНЫЙ компонент
 * ExercisesCatalog (4 модуля: Речь / Когнитивные навыки / Мимика / Моторика,
 * яркие кнопки, ИПР-галочки, Калибровка и Параметры у «Мимики»). Данные — ../exercises-data.
 * Поиск упражнения — на ОДНОЙ строке с заголовком; текст под заголовком убран
 * (по решению врача). Дублей каталога нет: одна реализация, две точки входа.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, BrainCircuit, Menu, Search, X } from 'lucide-react';
import ExercisesCatalog from './ExercisesCatalog';

// >>> CVX COLORS AUTO-BLOCK (не редактировать вручную) >>>
// String.raw — ОБЯЗАТЕЛЬНО: иначе JS в template literal съедает обратные слэши
// в селекторах (hover\:, \/40) и правила вариантов молча умирают.
// ЕДИНЫЙ блок для ВСЕХ страниц (кабинет врача, кабинет пациента, каталог,
// отчёт): какой бы файл ни загрузился первым — набор цветов одинаковый.
const CVX_COLORS_CSS = String.raw`
/* ==========================================================================
   cortivox-cvx.css — цвета кабинетов Cortivox (пациент и врач).
   Готово по демо UX «финал 25.10». НЕ трогает лендинг: это отдельные
   классы bg-cvx-*/text-cvx-*/..., которые нигде больше в проекте не встречаются.
   Подключение: в src/main.jsx после import "./index.css" добавить
     import "./cortivox-cvx.css";
   ========================================================================== */

.bg-cvx-accent{ background-color: #CFEFE0; }
.bg-cvx-accent\/10{ background-color: #CFEFE01A; }
.bg-cvx-background{ background-color: #F5FBF8; }
.bg-cvx-background\/95{ background-color: #F5FBF8F2; }
.bg-cvx-border{ background-color: #D8E2DE; }
.bg-cvx-card{ background-color: #FFFFFF; }
.bg-cvx-card\/60{ background-color: #FFFFFF99; }
.bg-cvx-muted{ background-color: #EDF4F1; }
.bg-cvx-muted-foreground\/50{ background-color: #576C6C80; }
.bg-cvx-popover{ background-color: #FFFFFF; }
.bg-cvx-primary{ background-color: #216858; }
.bg-cvx-primary-foreground\/20{ background-color: #F7FBF933; }
.bg-cvx-primary\/10{ background-color: #2168581A; }
.bg-cvx-primary\/25{ background-color: #21685840; }
.bg-cvx-primary\/40{ background-color: #21685866; }
.bg-cvx-primary\/80{ background-color: #216858CC; }
.bg-cvx-secondary{ background-color: #E8F2EE; }
.bg-cvx-secondary\/20{ background-color: #E8F2EE33; }
.bg-cvx-secondary\/30{ background-color: #E8F2EE4D; }
.bg-cvx-secondary\/40{ background-color: #E8F2EE66; }
.bg-cvx-secondary\/50{ background-color: #E8F2EE80; }
.bg-cvx-secondary\/60{ background-color: #E8F2EE99; }
.bg-cvx-sidebar{ background-color: #F0F9F6; }
.border-cvx-border{ border-color: #D8E2DE; }
.border-cvx-input{ border-color: #D8E2DE; }
.border-cvx-primary{ border-color: #216858; }
.divide-cvx-border{ border-color: #D8E2DE; }
.focus\:border-cvx-primary\/50:focus{ border-color: #21685880; }
.focus\:ring-cvx-ring:focus{ --tw-ring-color: #508E7E; }
.focus\:ring-cvx-ring\/20:focus{ --tw-ring-color: #508E7E33; }
.hover\:bg-cvx-accent:hover{ background-color: #CFEFE0; }
.hover\:bg-cvx-primary\/90:hover{ background-color: #216858E6; }
.hover\:bg-cvx-secondary:hover{ background-color: #E8F2EE; }
.hover\:bg-cvx-secondary\/50:hover{ background-color: #E8F2EE80; }
.hover\:bg-cvx-sidebar-accent:hover{ background-color: #CFEFE0; }
.hover\:border-cvx-destructive\/40:hover{ border-color: #DC262666; }
.hover\:border-cvx-primary\/40:hover{ border-color: #21685866; }
.hover\:border-cvx-primary\/50:hover{ border-color: #21685880; }
.hover\:text-cvx-destructive:hover{ color: #DC2626; }
.hover\:text-cvx-primary:hover{ color: #216858; }
.placeholder\:text-cvx-muted-foreground::placeholder{ color: #576C6C; }
.placeholder\:text-cvx-muted-foreground\/50::placeholder{ color: #576C6C80; }
.placeholder\:text-cvx-muted-foreground\/70::placeholder{ color: #576C6CB3; }
.ring-cvx-primary\/30{ --tw-ring-color: #2168584D; }
.stroke-cvx-primary{ stroke: #216858; }
.stroke-cvx-secondary{ stroke: #E8F2EE; }
.text-cvx-accent-foreground{ color: #094E3E; }
.text-cvx-destructive{ color: #DC2626; }
.text-cvx-foreground{ color: #182729; }
.text-cvx-muted-foreground{ color: #576C6C; }
.text-cvx-muted-foreground\/40{ color: #576C6C66; }
.text-cvx-muted-foreground\/60{ color: #576C6C99; }
.text-cvx-muted-foreground\/80{ color: #576C6CCC; }
.text-cvx-primary{ color: #216858; }
.text-cvx-primary-foreground{ color: #F7FBF9; }
.text-cvx-primary-foreground\/70{ color: #F7FBF9B3; }
.text-cvx-primary\/60{ color: #21685899; }
.text-cvx-secondary-foreground{ color: #18433A; }

/* Правки 26.10 — унификация с лендингом: чёрные заголовки, янтарное
   «Требуют внимания», логотип Cortivox.ai (как на лендинге) */
.bg-cvx-warn{ background-color: #FEF6E3; }
.bg-cvx-warn-card{ background-color: #FFFCF2; }
.border-cvx-warn{ border-color: #EFDFAE; }
.text-cvx-warn{ color: #B45309; }
.text-cvx-logo-ai{ color: #508E7E; }

/* Правки 26.10 v2 — как на лендинге: чёрный — для действий и активных
   состояний (кнопки, выбранные вкладки, логотип), зелёный — только акценты.
   ink = основной чёрный лендинга (shadcn neutral --primary, oklch 0.145 0 0) */
.bg-cvx-ink{ background-color: #0A0A0A; }
.bg-cvx-ink\/40{ background-color: #0A0A0A66; }
.hover\:bg-cvx-ink\/90:hover{ background-color: #0A0A0AE6; }
.text-cvx-ink{ color: #0A0A0A; }
.border-cvx-ink{ border-color: #0A0A0A; }
.hover\:text-cvx-ink:hover{ color: #0A0A0A; }

/* Единая кнопка «Назад» (везде одинаковая: отчёты, карточка пациента, чат) */
.cvx-back{ display:inline-flex; align-items:center; gap:.5rem; height:2.5rem; padding:0 1rem; border-radius:9999px; border:1px solid #D8E2DE; background:#FFFFFF; color:#0A0A0A; font-size:.875rem; font-weight:600; line-height:1; white-space:nowrap; flex:none; cursor:pointer; transition:background-color .15s ease, border-color .15s ease; }
.cvx-back:hover{ background:#E8F2EE; border-color:#C9DAD3; }
.cvx-back:disabled{ opacity:.5; }
.divide-cvx-border > :not([hidden]) ~ :not([hidden]) { border-color: #D8E2DE; }

/* Кастомные классы демо (не Tailwind) */
.cv-shadow { box-shadow: 0 1px 2px #10362f0a, 0 4px 16px #10362f0d; }
.cv-large { font-size: 1.125rem; }
.cv-large .cv-big-btn { border-radius: 1.25rem; height: 4rem; font-size: 1.1875rem; }
.cv-scroll::-webkit-scrollbar { width: 8px; }
.cv-scroll::-webkit-scrollbar-thumb { background: #d7e8e1; border-radius: 8px; }
`;
if (typeof document !== 'undefined') {
  // Версионная защита (26.10, v4): старый блок #cvx-colors (без версии = v0,
  // например из старого файла каталога) ЗАМЕНЯЕТСЯ этим блоком (v5). Раньше guard
  // «уже есть — пропустить» оставлял старые цвета навсегда, и чёрные кнопки/
  // логотип становились прозрачными, а белый текст — невидимым.
  const __cvxPrev = document.getElementById('cvx-colors');
  const __cvxPrevVer = __cvxPrev ? (Number(__cvxPrev.getAttribute('data-cvx-version') || '0') || 0) : 0;
  if (!__cvxPrev || __cvxPrevVer < 5) {
    if (__cvxPrev) __cvxPrev.parentNode.removeChild(__cvxPrev);
    const __cvxStyle = document.createElement('style');
    __cvxStyle.id = 'cvx-colors';
    __cvxStyle.setAttribute('data-cvx-version', '5');
    __cvxStyle.textContent = CVX_COLORS_CSS;
    document.head.appendChild(__cvxStyle);
  }
}
// <<< CVX COLORS AUTO-BLOCK <<<

function Logo() {
  return (
    <a href="/" className="flex items-center gap-2.5" aria-label="Cortivox.ai на главную">
      <span className="flex size-8 items-center justify-center rounded-[10px] bg-primary text-primary-foreground"><BrainCircuit className="size-4" /></span>
      <span className="text-[16px] font-semibold tracking-[-0.04em] text-primary">Cortivox<span className="text-accent">.ai</span></span>
    </a>
  );
}

export default function ExercisesPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Logo />
            <Link to="/" className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-primary">
              <ArrowLeft className="size-3.5" /> На главную
            </Link>
          </div>
          <nav className="hidden items-center gap-6 text-xs font-medium text-muted-foreground lg:flex" aria-label="Основная навигация">
            <a href="/#doctors" className="transition-colors hover:text-primary">Для Врачей</a>
            <a href="/#patients" className="transition-colors hover:text-primary">Для Пациентов</a>
            <Link to="/scales" className="transition-colors hover:text-primary">Шкалы</Link>
            <Link to="/exercises" className="text-primary">Тренажер</Link>
            <a href="/#about" className="transition-colors hover:text-primary">О Платформе</a>
          </nav>
          <a href="mailto:hello@cortivox.ai" className="hidden items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:flex">Запросить демо <ArrowUpRight className="size-3.5" /></a>
          <button type="button" className="rounded-lg p-2 text-primary lg:hidden" aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}</button>
        </div>
        {menuOpen && <nav className="flex flex-col gap-4 border-t border-border px-5 py-5 text-sm font-medium text-primary lg:hidden" aria-label="Мобильная навигация"><a href="/#doctors">Для Врачей</a><a href="/#patients">Для Пациентов</a><Link to="/scales">Шкалы</Link><Link to="/exercises">Тренажер</Link><a href="/#about">О Платформе</a><a href="/" className="text-muted-foreground">На главную</a><a href="mailto:hello@cortivox.ai" className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground">Запросить демо <ArrowUpRight className="size-3.5" /></a></nav>}
      </header>

      {/* Заголовок и поиск — на ОДНОЙ строке (поиск справа, на мобильном — под заголовком) */}
      <section className="mx-auto max-w-6xl px-5 pb-2 pt-4 lg:px-8 lg:pt-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <h1 className="text-balance text-3xl font-semibold leading-tight tracking-[-.06em] text-primary sm:text-4xl">Каталог упражнений</h1>
          <label className="relative block w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-cvx-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Поиск упражнения..."
              aria-label="Поиск упражнения"
              className="h-12 w-full rounded-2xl border border-cvx-border bg-cvx-card pl-10 pr-4 text-base text-cvx-foreground outline-none transition-all placeholder:text-cvx-muted-foreground/70 focus:border-cvx-primary/50 focus:ring-4 focus:ring-cvx-ring/20"
            />
          </label>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-14 pt-4 lg:px-8">
        <ExercisesCatalog query={query} onQueryChange={setQuery} />
      </section>

      <footer className="border-t border-border px-5 py-5 lg:px-8"><div className="mx-auto flex max-w-6xl flex-col gap-4 text-xs sm:flex-row sm:items-center sm:justify-between"><Logo /><p className="text-muted-foreground">© 2026 Cortivox.ai</p><div className="flex flex-wrap gap-4 text-muted-foreground"><a href="#" className="transition-colors hover:text-primary">Telegram</a><a href="mailto:hello@cortivox.ai" className="transition-colors hover:text-primary">Email</a><a href="#" className="transition-colors hover:text-primary">Политика конфиденциальности</a></div></div></footer>
    </main>
  );
}
